import { useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Settings, Plus, X } from 'lucide-react'
import { TemplateResponseData } from '@/services/inference.gen'
import { toast } from 'react-hot-toast'
import { cn } from '@/lib/utils'
import type { DashboardNewServerFormData } from './index'

type EnvVarsManagerProps = {
  form: UseFormReturn<DashboardNewServerFormData>
  selectedTemplate: TemplateResponseData
}

export const EnvVarsManager = ({
  form,
  selectedTemplate,
}: EnvVarsManagerProps) => {
  const [newEnvKey, setNewEnvKey] = useState('')
  const [newEnvValue, setNewEnvValue] = useState('')

  const handleAddEnvVar = () => {
    if (!newEnvKey.trim() || !newEnvValue.trim()) return

    const predefinedKeys =
      selectedTemplate?.init_command_override?.env_config?.map(
        (item) => item.name
      ) || []

    if (predefinedKeys.includes(newEnvKey.trim())) {
      toast.error(
        `"${newEnvKey.trim()}" is a predefined environment variable. Please use a different name.`
      )
      return
    }

    const currentEnvVars = form.getValues('envVars')
    form.setValue('envVars', {
      ...currentEnvVars,
      [newEnvKey.trim()]: newEnvValue.trim(),
    })
    setNewEnvKey('')
    setNewEnvValue('')
  }

  const handleRemoveEnvVar = (key: string) => {
    const predefinedKeys =
      selectedTemplate?.init_command_override?.env_config?.map(
        (item) => item.name
      ) || []

    if (predefinedKeys.includes(key)) {
      toast.error(
        `"${key}" is a predefined environment variable and cannot be removed.`
      )
      return
    }

    const currentEnvVars = form.getValues('envVars')
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [key]: _removed, ...rest } = currentEnvVars
    form.setValue('envVars', rest)
  }

  const customEnvVars = Object.entries(form.watch('envVars')).filter(
    ([key]) => {
      const predefinedKeys =
        selectedTemplate?.init_command_override?.env_config?.map(
          (item) => item.name
        ) || []
      return !predefinedKeys.includes(key)
    }
  )

  return (
    <div
      className={cn(
        'space-y-4 rounded-lg p-4 min-h-[90px] border transition-all',
        'bg-gradient-to-br from-muted/90 to-muted/40 border-border/40'
      )}
      data-testid="env-vars-card"
    >
      <div className="flex items-center gap-2 border-b border-border/40 pb-3">
        <div className="rounded-md p-1 bg-muted/90">
          <Settings className="h-5 w-5" />
        </div>
        <span className="text-sm font-semibold">Environment Variables</span>
      </div>

      {/* Predefined Environment Variables */}
      {selectedTemplate?.support_init_command_override &&
        selectedTemplate?.init_command_override?.env_config &&
        selectedTemplate.init_command_override.env_config.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Default Variables
              </span>
              <div className="flex-1 h-px bg-border/50" />
            </div>

            <div className="space-y-3">
              {selectedTemplate.init_command_override.env_config.map(
                (envItem) => {
                  const envKey = envItem.name
                  const overrideValue = form.watch('envVars')[envKey]
                  const isOverridden = overrideValue !== undefined
                  const isRequired = envItem.required

                  return (
                    <div key={envKey} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">
                          {envKey}
                        </span>
                        {isRequired && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200 font-medium">
                            Required
                          </span>
                        )}
                        <span
                          className={cn(
                            'text-xs px-1.5 py-0.5 rounded font-medium',
                            isOverridden
                              ? 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {isOverridden ? 'Custom' : 'Default'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Input
                          value={overrideValue || ''}
                          onChange={(e) => {
                            const newValue = e.target.value
                            const currentEnvVars = form.getValues('envVars')

                            if (newValue.trim() === '') {
                              if (!isRequired) {
                                delete currentEnvVars[envKey]
                                form.setValue('envVars', { ...currentEnvVars })
                              }
                            } else {
                              form.setValue('envVars', {
                                ...currentEnvVars,
                                [envKey]: newValue.trim(),
                              })
                            }
                          }}
                          placeholder={
                            isRequired
                              ? `Required${envItem.default ? ` (default: ${envItem.default})` : ''}`
                              : `Default: ${envItem.default}`
                          }
                          className={cn(
                            'h-8 text-xs flex-1',
                            isOverridden
                              ? 'border-primary/50 bg-primary/5'
                              : 'border-border/50',
                            isRequired &&
                              !overrideValue &&
                              'border-red-300 dark:border-red-700'
                          )}
                          data-testid={`env-config-${envKey}`}
                        />
                        {isOverridden && !isRequired && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              const currentEnvVars = form.getValues('envVars')
                              delete currentEnvVars[envKey]
                              form.setValue('envVars', { ...currentEnvVars })
                            }}
                            data-testid={`reset-env-config-${envKey}`}
                          >
                            Reset
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                }
              )}
            </div>
          </div>
        )}

      {/* Custom Environment Variables */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            Additional Variables
          </span>
          <div className="flex-1 h-px bg-border/50" />
        </div>

        <div className="space-y-2">
          {customEnvVars.length > 0 ? (
            customEnvVars.map(([key, value]) => (
              <div
                key={key}
                className="flex items-center gap-2 text-xs font-mono p-2 bg-background border border-border/50 rounded-md"
              >
                <span className="flex-1 truncate">
                  <span className="font-medium text-primary">{key}</span>
                  <span className="text-muted-foreground mx-1">=</span>
                  <span>{value}</span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => handleRemoveEnvVar(key)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))
          ) : (
            <div className="text-xs text-muted-foreground p-2 bg-background border border-dashed border-border/50 rounded-md text-center">
              No additional variables
            </div>
          )}
        </div>

        <div className="space-y-2">
          <span className="text-xs font-medium text-foreground">
            Add Variable:
          </span>
          <div className="grid grid-cols-[2fr_2fr_auto] gap-2">
            <Input
              value={newEnvKey}
              onChange={(e) => setNewEnvKey(e.target.value)}
              placeholder="VARIABLE_NAME"
              className="h-8 text-xs"
              data-testid="env-key-input"
            />
            <Input
              value={newEnvValue}
              onChange={(e) => setNewEnvValue(e.target.value)}
              placeholder="value"
              className="h-8 text-xs"
              data-testid="env-value-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddEnvVar}
              disabled={!newEnvKey.trim() || !newEnvValue.trim()}
              className="h-8 px-3"
              data-testid="add-env-var-button"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
